import { useCallback, useEffect, useRef, useState } from 'react'

// D-VIEWS-AUTOREFRESH (Maya round table, 2026-08-06): the single auto-refresh behaviour shared by
// every view (StationQueue system views + CustomViewTab custom/new views + RefillsTab). Polls every
// 15s, but only while the tab is visible (no background churn), refreshes immediately when the user
// returns to the tab, and never overlaps an in-flight refresh. A small jitter avoids a thundering
// herd across many open tabs. The caller's refresh must be SILENT (swap rows only) so scroll,
// selection, and the client-applied filters/sort are never disturbed.

export const VIEW_REFRESH_MS = 15000

export interface AutoRefresh {
  autoOn: boolean
  setAutoOn: (v: boolean) => void
  refreshing: boolean
  lastUpdated: number
  refreshNow: () => void
}

export function useAutoRefresh(
  onRefresh: () => void | Promise<void>,
  opts?: { intervalMs?: number; enabled?: boolean },
): AutoRefresh {
  const intervalMs = opts?.intervalMs ?? VIEW_REFRESH_MS
  const [autoOn, setAutoOn] = useState(opts?.enabled ?? true)
  const [refreshing, setRefreshing] = useState(false)
  const [lastUpdated, setLastUpdated] = useState<number>(() => Date.now())
  const inFlight = useRef(false)
  const cbRef = useRef(onRefresh)
  cbRef.current = onRefresh

  const refreshNow = useCallback(() => {
    if (inFlight.current) return
    inFlight.current = true
    setRefreshing(true)
    // The callback is invoked INSIDE the promise chain, not as an argument to Promise.resolve().
    // Evaluating it outside meant a synchronous throw escaped before `.finally` was attached,
    // leaving inFlight pinned true and `refreshing` stuck on — auto-refresh silently dead for the
    // life of the mount, with the spinner running. Async callbacks reject rather than throw, so
    // today's callers never hit it; a plain function that throws would kill the view's updates.
    void Promise.resolve()
      .then(() => cbRef.current())
      .then(() => setLastUpdated(Date.now()))
      .catch(() => { /* a failed refresh must not stop the next one; the view keeps its last rows */ })
      .finally(() => {
        inFlight.current = false
        setRefreshing(false)
      })
  }, [])

  useEffect(() => {
    if (!autoOn) return
    const period = intervalMs + Math.floor(Math.random() * 1500)
    const tick = () => {
      if (document.visibilityState === 'visible') refreshNow()
    }
    const onVisible = () => {
      if (document.visibilityState === 'visible') refreshNow()
    }
    const id = window.setInterval(tick, period)
    document.addEventListener('visibilitychange', onVisible)
    return () => {
      window.clearInterval(id)
      document.removeEventListener('visibilitychange', onVisible)
    }
  }, [autoOn, intervalMs, refreshNow])

  return { autoOn, setAutoOn, refreshing, lastUpdated, refreshNow }
}
